import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Card,
  TextField,
  InputAdornment,
  Grid,
  Tabs,
  Tab,
  Skeleton,
  IconButton,
  useMediaQuery,
  useTheme,
  Alert,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TwoWheelerRoundedIcon from '@mui/icons-material/TwoWheelerRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../hooks/useDebounce';
import { PageHeader } from '../../components/ui/PageHeader';
import { ErrorRetry } from '../../components/ui/ErrorRetry';
import { statusStyle } from '../../theme/orderStatus';
import {
  listPharmacyChainOrders,
  updatePharmacyChainOrderStatus,
  PharmacyChainOrderListItem,
  OrderStatus,
} from '../../api/pharmacyChainOrders';
import { PharmacyOrderDetailPanel } from './PharmacyOrderDetailPanel';
import { useNewOrderAlerts } from '../../hooks/useNewOrderAlerts';
import {
  OrderAlertsToggle,
  readAlertsEnabled,
} from '../../components/pharmacy/OrderAlertsToggle';

const KANBAN_COLUMNS: OrderStatus[] = ['PENDING', 'IN_PROGRESS', 'READY_FOR_PICKUP', 'COMPLETED'];

const STATUS_LABEL_KEY: Record<OrderStatus, string> = {
  PENDING: 'status.pending',
  IN_PROGRESS: 'status.inProgress',
  READY_FOR_PICKUP: 'status.readyForPickup',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
};

/**
 * Everything the COMPLETED column needs beyond its rows. `total` is the
 * server's count, not the fetched length; the loading/error flags exist so the
 * column never renders a confident "no orders" for data it has not got.
 */
interface CompletedColumnState {
  total: number;
  isLoading: boolean;
  isError: boolean;
}

const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: color ?? 'text.secondary',
      lineHeight: 1,
    }}
  >
    {children}
  </Typography>
);

export const PharmacyOrdersKanban: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<OrderStatus>('PENDING');

  const activeQuery = useQuery({
    queryKey: ['pharmacy-chain-orders', 'active', { search: debouncedSearch }],
    queryFn: () =>
      listPharmacyChainOrders({
        status: ['PENDING', 'IN_PROGRESS', 'READY_FOR_PICKUP'],
        search: debouncedSearch || undefined,
        limit: 100,
      }),
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  // COMPLETED is a short-term work log, not an archive: a rolling 24h window
  // keeps it useful across a shift boundary without unbounded growth. Sorted by
  // completedAt so what was just finished sits at the top.
  //
  // The window is dropped while searching. The server ANDs it into the same
  // `where` as the search predicate, so keeping it would make the search box —
  // the documented escape hatch for older orders — unable to reach them at all.
  // The query key varies on `search`, so the windowed and unwindowed responses
  // never share a cache entry.
  const completedQuery = useQuery({
    queryKey: ['pharmacy-chain-orders', 'completed', { search: debouncedSearch }],
    queryFn: () =>
      listPharmacyChainOrders({
        status: ['COMPLETED'],
        completedWithinHours: debouncedSearch ? undefined : 24,
        sort: 'completedAt',
        search: debouncedSearch || undefined,
        limit: 100,
      }),
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  const [alertsEnabled, setAlertsEnabled] = useState(readAlertsEnabled);

  // Detection is deliberately decoupled from what the board displays. A search
  // does not narrow the seeded window — the API applies the search predicate
  // inside `where` and only then takes the newest `limit` rows, so a filtered
  // response can surface an old order that was never in any prior response and
  // it would read as an arrival. Feeding detection from its own unfiltered
  // query removes that hazard at the source, and means leaving a term in the
  // search box no longer silently stops alerting the pharmacist.
  const alertsDetectionQuery = useQuery({
    queryKey: ['pharmacy-chain-orders', 'alerts-detection'],
    queryFn: () => listPharmacyChainOrders({ status: ['PENDING'], limit: 100 }),
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  const pendingOrderIds = React.useMemo(
    () => alertsDetectionQuery.data?.data.map((order) => order.id),
    [alertsDetectionQuery.data],
  );

  // `handleViewNewOrders` has to exist before the hook call that consumes it,
  // and it needs `dismiss`, which the hook returns. The ref breaks the cycle
  // and keeps the callback itself stable.
  const dismissAlertsRef = React.useRef<() => void>(() => {});
  const handleViewNewOrders = React.useCallback(() => {
    setSearch('');
    setActiveStatus('PENDING');
    dismissAlertsRef.current();
  }, []);

  const alerts = useNewOrderAlerts({
    pendingOrderIds,
    enabled: alertsEnabled,
    messages: {
      notificationTitle: t('kanban.alerts.notificationTitle'),
      notificationBody: (count: number) =>
        t('kanban.alerts.notificationBody', { count }),
      titleBadge: (count: number) => t('kanban.alerts.titleBadge', { count }),
    },
    // The notification promises to take them to the order; dismissing the
    // banner alone can strand them on the wrong mobile tab.
    onNotificationClick: handleViewNewOrders,
  });
  dismissAlertsRef.current = alerts.dismiss;

  const statusMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: OrderStatus }) =>
      updatePharmacyChainOrderStatus(orderId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chain-orders'] });
    },
  });

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;
    statusMutation.mutate({ orderId: draggableId, status: destination.droppableId as OrderStatus });
  };

  const ordersByStatus: Record<OrderStatus, PharmacyChainOrderListItem[]> = {
    PENDING: [], IN_PROGRESS: [], READY_FOR_PICKUP: [], COMPLETED: [], CANCELLED: [],
  };
  // The two queries settle independently — after a COMPLETED transition, or on
  // the two unsynchronised 15s polls, the completed query can already carry an
  // order the still-stale active query also carries. Rendering both copies puts
  // two <Draggable> with the same draggableId in one DragDropContext, a
  // registry collision whose only symptom in a production build is a
  // mis-targeted drag. The completed query's view wins: for a just-completed
  // order it is the newer fact.
  const completedIds = new Set(completedQuery.data?.data.map((o) => o.id) ?? []);
  completedQuery.data?.data.forEach((order) => {
    if (ordersByStatus[order.status]) ordersByStatus[order.status].push(order);
  });
  activeQuery.data?.data.forEach((order) => {
    if (completedIds.has(order.id)) return;
    if (ordersByStatus[order.status]) ordersByStatus[order.status].push(order);
  });

  // The completed column's badge and footer both read the server's total, not
  // the fetched length, so a truncated window can't under-report silently.
  // `isLoading`/`isError` travel with it: a column whose query has not settled
  // must not claim to be empty.
  const completed: CompletedColumnState = {
    total: completedQuery.data?.total ?? 0,
    isLoading: completedQuery.isLoading,
    isError: completedQuery.isError,
  };

  const isForbidden = (activeQuery.error as any)?.response?.status === 403;
  const isFetching =
    activeQuery.isFetching || completedQuery.isFetching || alertsDetectionQuery.isFetching;
  // Deliberately excludes the detection query: it backs no column, so a failure
  // there must not claim the board failed to load.
  const isError = activeQuery.isError || completedQuery.isError;
  const refetchAll = () => {
    activeQuery.refetch();
    completedQuery.refetch();
    alertsDetectionQuery.refetch();
  };

  if (isForbidden) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 5, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Eyebrow color="error.main">{t('kanban.pharmacyBadge')}</Eyebrow>
          <Typography variant="h3" sx={{ color: 'text.headline', mt: 1.5, mb: 1.5 }}>
            {t('kanban.noChainAssigned')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        title={t('kanban.orderManagement')}
        subtitle={
          activeQuery.data
            ? `${t('kanban.totalOrders')}: ${activeQuery.data.total} · ${t('kanban.autoRefresh')}`
            : t('kanban.autoRefresh') as string
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrderAlertsToggle enabled={alertsEnabled} onChange={setAlertsEnabled} />
            <IconButton
              aria-label={t('kanban.refresh') as string}
              onClick={refetchAll}
              disabled={isFetching}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: 'text.headline',
                '&:hover': { bgcolor: 'primary.softBg' },
              }}
            >
              <RefreshRoundedIcon
                sx={{
                  fontSize: 20,
                  animation: isFetching ? 'spin 1.2s linear infinite' : 'none',
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Box>
        }
      />

      {alerts.newCount > 0 && (
        <Alert
          severity="info"
          icon={<NotificationsActiveRoundedIcon fontSize="inherit" />}
          sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button color="inherit" size="small" onClick={handleViewNewOrders}>
                {t('kanban.alerts.viewOrders')}
              </Button>
              <IconButton
                size="small"
                color="inherit"
                aria-label={t('kanban.alerts.dismiss') as string}
                onClick={alerts.dismiss}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          {t('kanban.alerts.banner', { count: alerts.newCount })}
        </Alert>
      )}

      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2.5, md: 3 },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <TextField
          fullWidth
          size="medium"
          variant="outlined"
          placeholder={t('kanban.searchPlaceholder') as string}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: { bgcolor: 'background.paper', fontSize: '0.95rem' },
          }}
        />
      </Paper>

      {isError && !isForbidden && (
        <Box sx={{ mb: 2 }}>
          <ErrorRetry
            message={t('kanban.failedToLoad') as string}
            onRetry={refetchAll}
            isLoading={isFetching}
          />
        </Box>
      )}

      {activeQuery.isLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} lg={3} key={i}>
              <Skeleton variant="rectangular" height={520} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : isMobile ? (
        <MobileKanban
          activeStatus={activeStatus}
          setActiveStatus={setActiveStatus}
          ordersByStatus={ordersByStatus}
          onCardClick={setSelectedOrderId}
          completed={completed}
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Grid container spacing={2}>
            {KANBAN_COLUMNS.map((status, colIdx) => (
              <Grid item xs={12} md={6} lg={3} key={status}>
                <KanbanColumn
                  status={status}
                  orders={ordersByStatus[status]}
                  onCardClick={setSelectedOrderId}
                  colIdx={colIdx}
                  total={status === 'COMPLETED' ? completed.total : undefined}
                  isLoading={status === 'COMPLETED' && completed.isLoading}
                  isError={status === 'COMPLETED' && completed.isError}
                />
              </Grid>
            ))}
          </Grid>
        </DragDropContext>
      )}

      <PharmacyOrderDetailPanel
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
    </Container>
  );
};

interface MobileKanbanProps {
  activeStatus: OrderStatus;
  setActiveStatus: (s: OrderStatus) => void;
  ordersByStatus: Record<OrderStatus, PharmacyChainOrderListItem[]>;
  onCardClick: (orderId: string) => void;
  completed: CompletedColumnState;
}

const MobileKanban: React.FC<MobileKanbanProps> = ({
  activeStatus,
  setActiveStatus,
  ordersByStatus,
  onCardClick,
  completed,
}) => {
  const { t } = useTranslation();
  const activeStyle = statusStyle(activeStatus);
  // A phone gets the same accounting as the desktop board: the COMPLETED tab
  // badge reads the server's total and a footer names what was left out, so the
  // 24h window and the 100-row limit are never dropped silently.
  const completedShown = ordersByStatus.COMPLETED.length;
  const isCompletedUnsettled = completed.isLoading || completed.isError;
  return (
    <>
      <Box
        sx={{
          mb: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeStatus}
          onChange={(_, v) => setActiveStatus(v as OrderStatus)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { height: 3, bgcolor: activeStyle.fg } }}
          sx={{
            minHeight: 52,
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontWeight: 600,
              letterSpacing: '0.01em',
              color: 'text.secondary',
            },
            '& .Mui-selected': { color: 'text.headline !important' },
          }}
        >
          {KANBAN_COLUMNS.map((s) => {
            const style = statusStyle(s);
            return (
              <Tab
                key={s}
                value={s}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: style.fg,
                      }}
                    />
                    {t(STATUS_LABEL_KEY[s])}
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: 'text.secondary',
                        fontVariant: 'tabular-nums',
                      }}
                    >
                      {s === 'COMPLETED' ? completed.total : ordersByStatus[s].length}
                    </Box>
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Box>
      <Box>
        {ordersByStatus[activeStatus].length === 0 ? (
          // Same rule as the desktop column: an unsettled COMPLETED query means
          // "unknown", not "nothing was completed today".
          activeStatus === 'COMPLETED' && isCompletedUnsettled ? null : (
            <Paper
              sx={{
                p: 5,
                textAlign: 'center',
                bgcolor: 'background.paper',
                border: '1px dashed',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('kanban.noOrders', { status: t(STATUS_LABEL_KEY[activeStatus]) })}
              </Typography>
            </Paper>
          )
        ) : (
          ordersByStatus[activeStatus].map((order, idx) => (
            <Box
              key={order.id}
              sx={{
                animation: 'customerStaggerIn 240ms ease-out both',
                animationDelay: `${Math.min(idx, 10) * 40}ms`,
              }}
            >
              <OrderCardStatic order={order} onClick={() => onCardClick(order.id)} />
            </Box>
          ))
        )}
        {activeStatus === 'COMPLETED' && completed.total > completedShown && (
          <Typography
            variant="caption"
            sx={{ display: 'block', px: 0.5, py: 1, color: 'text.secondary' }}
          >
            {t('kanban.showingOf', { shown: completedShown, total: completed.total })}
          </Typography>
        )}
      </Box>
    </>
  );
};

interface KanbanColumnProps {
  status: OrderStatus;
  orders: PharmacyChainOrderListItem[];
  onCardClick: (orderId: string) => void;
  colIdx: number;
  /** Server-side total for this column. Defaults to the fetched length. */
  total?: number;
  /** This column's own query is still loading — show placeholders, not "empty". */
  isLoading?: boolean;
  /** This column's own query failed — show nothing, not "empty". The page-level
   *  error banner is what explains why. */
  isError?: boolean;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status, orders, onCardClick, colIdx, total, isLoading, isError,
}) => {
  const { t } = useTranslation();
  const style = statusStyle(status);
  const shown = orders.length;
  const columnTotal = total ?? shown;
  return (
    <Paper
      sx={{
        position: 'relative',
        p: 0,
        minHeight: 560,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 1px 0 rgba(13,27,53,0.04)',
        overflow: 'hidden',
        animation: 'customerStaggerIn 320ms ease-out both',
        animationDelay: `${colIdx * 60}ms`,
      }}
    >
      <Box
        sx={{
          height: 3,
          width: '100%',
          bgcolor: style.fg,
        }}
      />
      <Box sx={{ p: 2, pb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Eyebrow color={style.fg}>{t(STATUS_LABEL_KEY[status])}</Eyebrow>
          <Box
            component="span"
            sx={{
              minWidth: 22,
              height: 22,
              borderRadius: '999px',
              bgcolor: style.bg,
              color: style.fg,
              fontWeight: 700,
              fontSize: '0.72rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.75,
              fontVariant: 'tabular-nums',
            }}
          >
            {columnTotal}
          </Box>
        </Box>
      </Box>
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              minHeight: 460,
              // The board is a fixed-height workspace: columns scroll, the page
              // does not grow with the longest one.
              height: 'calc(100vh - 320px)',
              overflowY: 'auto',
              px: 1.25,
              pb: 1.25,
              bgcolor: snapshot.isDraggingOver ? style.bg : 'transparent',
              transition: 'background-color 160ms ease-out',
              borderRadius: 1,
            }}
          >
            {orders.length === 0 ? (
              // "Sin pedidos" is a claim about the data. Make it only when the
              // column's query has actually settled: on a pharmacy floor,
              // "nothing was completed today" and "this column is broken" look
              // identical and lead to very different actions.
              isLoading ? (
                <Box sx={{ px: 0.75 }}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={104}
                      sx={{ borderRadius: 2, mb: 1.25 }}
                    />
                  ))}
                </Box>
              ) : isError ? null : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    mx: 0.75,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'text.disabled',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {t('kanban.columnEmpty')}
                  </Typography>
                </Box>
              )
            ) : (
              orders.map((order, index) => (
                <OrderCardDraggable
                  key={order.id}
                  order={order}
                  index={index}
                  onClick={() => onCardClick(order.id)}
                />
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
      {columnTotal > shown && (
        <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('kanban.showingOf', { shown, total: columnTotal })}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

interface OrderCardProps {
  order: PharmacyChainOrderListItem;
  onClick: () => void;
}

const OrderCardStatic: React.FC<OrderCardProps> = ({ order, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      mb: 1.5,
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 140ms ease-out, box-shadow 140ms ease-out',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(13,27,53,0.10)' },
    }}
  >
    <OrderCardBody order={order} />
  </Card>
);

const OrderCardDraggable: React.FC<OrderCardProps & { index: number }> = ({ order, index, onClick }) => (
  <Draggable draggableId={order.id} index={index}>
    {(provided, snapshot) => (
      <Card
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        sx={{
          mb: 1.25,
          cursor: 'grab',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          transition: 'transform 140ms ease-out, box-shadow 140ms ease-out',
          boxShadow: snapshot.isDragging
            ? '0 10px 28px rgba(13,27,53,0.18)'
            : '0 1px 0 rgba(13,27,53,0.05)',
          transform: snapshot.isDragging ? 'rotate(-1deg)' : 'none',
          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(13,27,53,0.10)' },
        }}
        onClick={onClick}
      >
        <OrderCardBody order={order} />
      </Card>
    )}
  </Draggable>
);

const OrderCardBody: React.FC<{ order: PharmacyChainOrderListItem }> = ({ order }) => {
  const { t } = useTranslation();
  const style = statusStyle(order.status);
  const isPickup = order.fulfillmentType === 'PICKUP';
  return (
    <Box sx={{ display: 'flex', minHeight: 0 }}>
      <Box
        sx={{
          width: 4,
          flexShrink: 0,
          bgcolor: style.fg,
        }}
      />
      <Box sx={{ flex: 1, p: 1.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace',
              color: 'text.disabled',
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}
          >
            #{order.trackingUUID.slice(0, 8).toUpperCase()}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
              fontVariant: 'tabular-nums',
            }}
          >
            {new Date(order.createdAt).toLocaleString('es-DO', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
            fontWeight: 500,
            fontSize: '1.05rem',
            lineHeight: 1.25,
            letterSpacing: '-0.015em',
            color: 'text.headline',
            mb: 0.75,
          }}
        >
          {order.patientName}
        </Typography>
        {order.doctor && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
            <LocalHospitalRoundedIcon sx={{ fontSize: 14, mr: 0.75 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Dr. {order.doctor.firstName} {order.doctor.lastName}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: 'primary.softBg',
              color: 'primary.dark',
            }}
          >
            {isPickup ? (
              <StorefrontRoundedIcon sx={{ fontSize: 14 }} />
            ) : (
              <TwoWheelerRoundedIcon sx={{ fontSize: 14 }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark' }}>
              {isPickup ? t('kanban.pickup') : t('kanban.delivery')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
